import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FadeIn, StaggerContainer, StaggerItem, RevealText, SlideReveal } from '../components/AnimatedSection';
import { FloatingHexagons, HexShape } from '../components/HexagonLogo';
import { MarqueeText } from '../components/MarqueeScroll';
import { ParallaxImage, RevealImage, WordReveal, MagneticButton } from '../components/ScrollEffects';
import { HiArrowRight, HiCheck } from 'react-icons/hi';

const services = [
  {
    num: '01', title: 'AI Video Production',
    desc: 'AI video presenters, product demos, factory showcases & social reels — broadcast quality at a fraction of traditional cost.',
    features: ['AI Presenters with natural speech & gestures', 'Product demo & factory showcase videos', 'Social media reels optimized for engagement', 'Factory tour & corporate profile videos'],
    tags: ['AI Presenters', 'Product Films', 'Social Reels', 'Factory Tours'],
    image: 'https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?w=800&q=80',
  },
  {
    num: '02', title: 'Market Research & Intelligence',
    desc: 'Competitor mapping, customer segmentation, geography & pain point analysis — so every decision is evidence-based.',
    features: ['Comprehensive competitor analysis & mapping', 'Customer segmentation & persona creation', 'Pain point identification & opportunity discovery', 'Geography-based market intelligence'],
    tags: ['Competitors', 'Segments', 'Pain Points', 'Geography'],
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80',
  },
  {
    num: '03', title: 'AI Content Creation',
    desc: 'SEO content, social posts, brochures, catalogues & campaign content — AI-powered, consistently on-brand.',
    features: ['SEO-optimized blog posts & articles', 'Social media content calendars & posts', 'Professional brochures & catalogues', 'Brand-consistent campaign content'],
    tags: ['SEO Content', 'Social Media', 'Brochures', 'Catalogues'],
    image: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800&q=80',
  },
  {
    num: '04', title: 'Web Design & Development',
    desc: 'Fast, beautiful, conversion-optimised websites built in days — not months. Ongoing support included.',
    features: ['Conversion-optimized responsive design', 'Rapid development & deployment', 'Ongoing maintenance & support', 'SEO-ready architecture'],
    tags: ['Design', 'Development', 'Maintenance', 'Support'],
    image: 'https://images.unsplash.com/photo-1547658719-da2b51169166?w=800&q=80',
  },
];

export default function Services() {
  return (
    <div className="page-wrapper">
      {/* Hero */}
      <section style={styles.heroSection}>
        <div style={styles.heroBg}>
          <motion.img
            src="https://images.unsplash.com/photo-1553877522-43269d4ea984?w=1920&q=80"
            alt="Digital services"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            initial={{ scale: 1.1 }}
            animate={{ scale: 1 }}
            transition={{ duration: 1.5, ease: [0.22, 1, 0.36, 1] }}
          />
          <div style={styles.heroOverlay} />
        </div>
        <FloatingHexagons />
        <div className="container" style={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>
          <FadeIn>
            <div className="section-num" style={{ justifyContent: 'center' }}>01 &mdash; SERVICES</div>
          </FadeIn>
          <RevealText>
            <h1 className="section-title" style={{ fontSize: 'clamp(2.8rem, 6vw, 4.2rem)' }}>
              Four services. One mission:<br />
              <span className="gradient-text">Make you impossible to ignore.</span>
            </h1>
          </RevealText>
          <FadeIn delay={0.3}>
            <p className="section-subtitle" style={{ margin: '0 auto' }}>
              End-to-end digital transformation for manufacturing brands.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Service blocks with images */}
      <section style={{ paddingBottom: 0 }}>
        <div className="container">
          {services.map((service, i) => (
            <React.Fragment key={i}>
              <SlideReveal direction={i % 2 === 0 ? 'left' : 'right'} delay={0.1}>
                <motion.div
                  className="grid-service-block"
                  style={styles.serviceBlock}
                  whileHover={{ borderColor: 'rgba(62, 117, 98, 0.15)' }}
                >
                  <div>
                    <span style={styles.num}>{service.num}</span>
                    <HexShape size={40} color="#3E7562" opacity={0.15} />
                    <h2 style={styles.title}>{service.title}</h2>
                    <p style={styles.desc}>{service.desc}</p>
                    <div style={styles.tags}>
                      {service.tags.map((tag, j) => (
                        <span key={j} style={styles.tag}>{tag}</span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <RevealImage
                      src={service.image}
                      alt={service.title}
                      style={{ width: '100%', height: 240, marginBottom: 24 }}
                    />
                    <h4 style={styles.featTitle}>What's Included</h4>
                    {service.features.map((f, j) => (
                      <div key={j} style={styles.featItem}>
                        <HiCheck size={14} color="#3E7562" style={{ flexShrink: 0, marginTop: 3 }} />
                        <span style={styles.featText}>{f}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </SlideReveal>

              {/* Parallax break between services */}
              {i < services.length - 1 && i % 2 === 0 && (
                <ParallaxImage
                  src={parallaxBreaks[Math.floor(i / 2)]}
                  alt="Service detail"
                  height={300}
                  speed={0.2}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* Platform Marquee */}
      <section style={{ borderTop: '1px solid rgba(62,117,98,0.08)', borderBottom: '1px solid rgba(62,117,98,0.08)', marginTop: 80 }}>
        <MarqueeText texts={['Instagram', 'LinkedIn', 'WhatsApp', 'Facebook', 'Website', 'YouTube']} speed={30} />
      </section>

      {/* CTA */}
      <section className="section" style={{ background: '#111112' }}>
        <div className="container">
          <FadeIn>
            <div style={styles.ctaBlock}>
              <div style={{ textAlign: 'center' }}>
                <WordReveal
                  text="Ready to get started?"
                  className="section-title"
                  style={{ fontSize: 'clamp(2rem, 4.5vw, 3rem)', fontWeight: 800, letterSpacing: '-1.5px', lineHeight: 1.15, display: 'block', marginBottom: 16 }}
                />
                <p style={{ color: '#888', marginBottom: 36 }}>
                  Let's discuss how we can transform your digital presence.
                </p>
                <MagneticButton>
                  <Link to="/contact" className="btn btn-primary">
                    Book a Discovery Call <HiArrowRight />
                  </Link>
                </MagneticButton>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}

const parallaxBreaks = [
  'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=1920&q=80',
  'https://images.unsplash.com/photo-1504384764405-5ec5a1860e41?w=1920&q=80',
];

const styles = {
  heroSection: {
    minHeight: '65vh',
    display: 'flex',
    alignItems: 'center',
    paddingTop: 180,
    paddingBottom: 80,
    position: 'relative',
    overflow: 'hidden',
  },
  heroBg: {
    position: 'absolute',
    inset: 0,
    zIndex: 0,
  },
  heroOverlay: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(180deg, rgba(11,11,11,0.88) 0%, rgba(11,11,11,0.75) 50%, rgba(11,11,11,0.95) 100%)',
  },
  serviceBlock: {
    padding: '56px 48px',
    borderBottom: '1px solid rgba(255,255,255,0.04)',
    transition: '0.4s',
  },
  num: { fontSize: '0.7rem', fontWeight: 600, color: '#3E7562', letterSpacing: 2, display: 'block', marginBottom: 12 },
  title: { fontSize: '1.6rem', fontWeight: 700, marginBottom: 12, marginTop: 12, letterSpacing: '-0.5px' },
  desc: { fontSize: '0.95rem', color: '#888', lineHeight: 1.8, marginBottom: 20, maxWidth: 450 },
  tags: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  tag: { padding: '5px 14px', border: '1px solid rgba(62,117,98,0.15)', color: '#777', fontSize: '0.72rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5 },
  featTitle: { fontSize: '0.75rem', fontWeight: 600, color: '#777', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 20 },
  featItem: { display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 },
  featText: { fontSize: '0.9rem', color: '#999', lineHeight: 1.4 },
  ctaBlock: { padding: '80px 48px', background: '#161618', border: '1px solid rgba(255,255,255,0.04)' },
};
